import { useMemo, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BASE_CURRENCY, validateAssetInput, type AssetCategory, type AssetInput } from "@torre/assets";

import Modal from "../../../components/base/Modal";
import { Input, Select, Textarea } from "../../../components/ui";
import { useActivosMutation, useSuppliers } from "../hooks/useActivos";
import { handleSaveError } from "../lib/conflicts";
import { fieldErrors, optionalText, parseAmount, parseInteger } from "../lib/format";
import { categoryOptions, langOf } from "../lib/vocab";
import { activosApi, type AssetRow } from "../services/activosApi";
import { FormBlock, FormFooter } from "./shared";

// ============================================================================
// Alta y edición de un activo (modal de dos columnas). Archivo común, idéntico
// en CC-CONSOLE y WAYAK. Valida con `validateAssetInput` de @torre/assets antes
// de mandar, y el API vuelve a validar. Sin `feat:activos.costos` no se pintan
// ni se mandan los montos (D6). Un 409 ofrece ir al activo o al dispositivo
// que ya tiene esa serie.
// ============================================================================

interface AssetFormModalProps {
  /** Con activo, edición; sin él, alta. */
  asset?: AssetRow | null;
  canSeeCosts: boolean;
  onClose: () => void;
  onSaved?: (asset: AssetRow) => void;
}

interface FormState {
  name: string;
  category: string;
  brand: string;
  model: string;
  serial: string;
  notes: string;
  supplierId: string;
  date: string;
  currency: string;
  unitCost: string;
  exchangeRate: string;
  uuid: string;
  serie: string;
  folio: string;
  warrantyUntil: string;
  usefulLifeMonths: string;
  nextDueAt: string;
}

const str = (v: unknown) => (v == null ? "" : String(v));

function initialState(asset?: AssetRow | null): FormState {
  const p = asset?.purchase;
  return {
    name: str(asset?.name),
    category: str(asset?.category),
    brand: str(asset?.brand),
    model: str(asset?.model),
    serial: str(asset?.serial),
    notes: str(asset?.notes),
    supplierId: str(p?.supplierId),
    date: str(p?.date),
    currency: p?.currency || BASE_CURRENCY,
    unitCost: str(p?.unitCost),
    exchangeRate: str(p?.exchangeRate),
    uuid: str(p?.invoice?.uuid),
    serie: str(p?.invoice?.serie),
    folio: str(p?.invoice?.folio),
    warrantyUntil: str(asset?.warranty?.until),
    usefulLifeMonths: str(asset?.depreciation?.usefulLifeMonths),
    nextDueAt: str(asset?.maintenance?.nextDueAt),
  };
}

/**
 * Cuerpo para el API. Con la compra ligada (`purchaseId`), proveedor, fecha,
 * factura, moneda y tipo de cambio son de la compra: no se mandan.
 */
export function buildAssetPayload(f: FormState, opts: { canSeeCosts: boolean; linkedPurchase: boolean }): AssetInput {
  const payload: AssetInput = {
    name: f.name,
    category: (f.category || undefined) as AssetCategory | undefined,
    brand: optionalText(f.brand),
    model: optionalText(f.model),
    serial: optionalText(f.serial),
    notes: optionalText(f.notes),
    warranty: { until: optionalText(f.warrantyUntil) },
    depreciation: { usefulLifeMonths: parseInteger(f.usefulLifeMonths) },
    maintenance: { nextDueAt: optionalText(f.nextDueAt) },
  };
  if (opts.linkedPurchase) {
    if (opts.canSeeCosts) payload.purchase = { unitCost: parseAmount(f.unitCost) };
    return payload;
  }
  const invoice = f.uuid.trim() || f.serie.trim() || f.folio.trim() ? { uuid: optionalText(f.uuid), serie: optionalText(f.serie), folio: optionalText(f.folio) } : null;
  payload.purchase = {
    supplierId: optionalText(f.supplierId),
    date: optionalText(f.date),
    invoice,
    currency: optionalText(f.currency) || BASE_CURRENCY,
  };
  if (opts.canSeeCosts) {
    payload.purchase.unitCost = parseAmount(f.unitCost);
    payload.purchase.exchangeRate = parseAmount(f.exchangeRate);
  }
  return payload;
}

export function AssetFormModal({ asset, canSeeCosts, onClose, onSaved }: AssetFormModalProps) {
  const { t, i18n } = useTranslation("activos");
  const lang = langOf(i18n.language);
  const navigate = useNavigate();
  const isEdit = Boolean(asset?.id);
  const linkedPurchase = Boolean(asset?.purchase?.purchaseId);
  const [form, setForm] = useState<FormState>(() => initialState(asset));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const suppliers = useSuppliers({});

  const save = useActivosMutation((input: AssetInput) => (isEdit ? activosApi.update(asset!.id, input) : activosApi.create(input)));

  const set = (k: keyof FormState) => (e: { target: { value: string } }) => setForm((prev) => ({ ...prev, [k]: e.target.value }));
  const supplierOptions = useMemo(
    () => (suppliers.data?.items || []).map((s) => ({ value: s.id, label: s.rfc ? `${s.name} · ${s.rfc}` : s.name })),
    [suppliers.data],
  );

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = buildAssetPayload(form, { canSeeCosts, linkedPurchase });
    const checked = validateAssetInput(payload, { mode: isEdit ? "update" : "create", canSeeCosts });
    if (!checked.ok) {
      setErrors(fieldErrors(checked.errors, lang));
      return;
    }
    setErrors({});
    try {
      const saved = await save.mutateAsync(payload);
      toast.success(t(isEdit ? "assetForm.updated" : "assetForm.created"));
      onSaved?.(saved);
      onClose();
    } catch (err) {
      const outcome = await handleSaveError(err, t, { navigate });
      if (outcome.kind === "fields") setErrors(fieldErrors(outcome.errors, lang));
      if (outcome.kind === "redirected") onClose();
    }
  };

  const err = (field: string) => errors[field];

  return (
    <Modal
      title={t(isEdit ? "assetForm.editTitle" : "assetForm.createTitle")}
      onClose={onClose}
      width="w-full max-w-3xl"
      content={
        <form onSubmit={onSubmit} noValidate data-testid="asset-form">
          <FormBlock title={t("assetForm.blocks.data")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input label={t("fields.name")} required value={form.name} onChange={set("name")} error={err("name")} containerClassName="sm:col-span-2" />
              <Select
                label={t("fields.category")}
                required
                value={form.category}
                onChange={set("category")}
                options={categoryOptions(lang)}
                placeholder={t("common.choose")}
                error={err("category")}
              />
              <Input label={t("fields.serial")} value={form.serial} onChange={set("serial")} error={err("serial")} hint={t("assetForm.serialHint")} />
              <Input label={t("fields.brand")} value={form.brand} onChange={set("brand")} error={err("brand")} />
              <Input label={t("fields.model")} value={form.model} onChange={set("model")} error={err("model")} />
            </div>
          </FormBlock>

          <FormBlock title={t("assetForm.blocks.purchase")}>
            {linkedPurchase ? (
              <p className="text-xs text-content-muted" data-testid="asset-form-linked">
                {t("assetForm.linkedPurchase")}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Select
                  label={t("fields.supplier")}
                  value={form.supplierId}
                  onChange={set("supplierId")}
                  options={[{ value: "", label: t("common.none") }, ...supplierOptions]}
                  error={err("purchase.supplierId")}
                />
                <Input label={t("fields.purchaseDate")} type="date" value={form.date} onChange={set("date")} error={err("purchase.date")} />
                <Input label={t("fields.invoiceUuid")} value={form.uuid} onChange={set("uuid")} error={err("purchase.invoice.uuid")} containerClassName="sm:col-span-2" />
                <Input label={t("fields.invoiceSerie")} value={form.serie} onChange={set("serie")} error={err("purchase.invoice.serie")} />
                <Input label={t("fields.invoiceFolio")} value={form.folio} onChange={set("folio")} error={err("purchase.invoice.folio")} />
                <Input label={t("fields.currency")} value={form.currency} onChange={set("currency")} maxLength={3} error={err("purchase.currency")} />
              </div>
            )}
            {canSeeCosts ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2" data-testid="asset-form-costs">
                <Input
                  label={t("fields.unitCost")}
                  inputMode="decimal"
                  value={form.unitCost}
                  onChange={set("unitCost")}
                  error={err("purchase.unitCost")}
                />
                {!linkedPurchase ? (
                  <Input
                    label={t("fields.exchangeRate")}
                    inputMode="decimal"
                    value={form.exchangeRate}
                    onChange={set("exchangeRate")}
                    error={err("purchase.exchangeRate")}
                    hint={t("assetForm.exchangeRateHint")}
                  />
                ) : null}
              </div>
            ) : null}
          </FormBlock>

          <FormBlock title={t("assetForm.blocks.lifecycle")}>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <Input label={t("fields.warrantyUntil")} type="date" value={form.warrantyUntil} onChange={set("warrantyUntil")} error={err("warranty.until")} />
              <Input
                label={t("fields.usefulLifeMonths")}
                inputMode="numeric"
                value={form.usefulLifeMonths}
                onChange={set("usefulLifeMonths")}
                error={err("depreciation.usefulLifeMonths")}
                hint={t("assetForm.usefulLifeHint")}
              />
              <Input label={t("fields.nextMaintenance")} type="date" value={form.nextDueAt} onChange={set("nextDueAt")} error={err("maintenance.nextDueAt")} />
            </div>
            <Textarea label={t("fields.notes")} value={form.notes} onChange={set("notes")} rows={3} error={err("notes")} />
          </FormBlock>

          <FormFooter
            onCancel={onClose}
            submitting={save.isPending}
            submitLabel={t(isEdit ? "common.save" : "assetForm.create")}
            submitTestId="asset-form-submit"
          />
        </form>
      }
    />
  );
}
